import React, { ReactNode } from 'react';
import {
  normalizeOfficialRisUrl,
  prepareRisMarkdown,
  safeHttpUrl,
} from '../services/risViewerLinks';
import { DOCUMENT_MARKER_PARTS_SOURCE, DOCUMENT_MARKER_SOURCE } from '../services/documentMarker';

const inlinePattern = new RegExp(
  '(`[^`\\n]+`'
  + `|${DOCUMENT_MARKER_SOURCE}`
  + '|\\[[^\\]\\n]+\\]\\(https?:\\/\\/[^\\s)]+\\)|\\*\\*[^*\\n]+\\*\\*|\\*[^*\\n]+\\*)',
  'gi',
);

/** Die Marke als Ganzes, zerlegt in Kennung, Titel und Adresse. */
const documentMarkerParts = new RegExp(`^${DOCUMENT_MARKER_PARTS_SOURCE}$`, 'i');

const inline = (
  text: string,
  onOpenRisSource?: (url: string) => void,
  onOpenDocumentSource?: (documentId: string) => void,
): ReactNode[] => text.split(inlinePattern).filter(Boolean).map((part, index) => {
  if (part.startsWith('`') && part.endsWith('`')) {
    return <code key={index} className="rounded bg-warm-100 px-1 py-0.5 font-mono text-[0.92em]">{part.slice(1, -1)}</code>;
  }
  const document = documentMarkerParts.exec(part);
  if (document) {
    const label = document[2]?.trim() || 'Dokument anzeigen';
    const externalUrl = safeHttpUrl(document[3]?.trim());
    if (!onOpenDocumentSource) return <React.Fragment key={index}>{label}</React.Fragment>;
    return (
      <React.Fragment key={index}>
        <button
          type="button"
          data-document-preview={document[1]}
          onClick={() => onOpenDocumentSource(document[1])}
          className="font-medium text-primary underline decoration-accent/50 underline-offset-2 hover:text-accent-dark"
        >{label}</button>
        {externalUrl && <>{' '}<a href={externalUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-muted underline underline-offset-2 hover:text-primary">Originalquelle</a></>}
      </React.Fragment>
    );
  }
  const link = /^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/.exec(part);
  if (link) {
    const risHref = normalizeOfficialRisUrl(link[2]);
    const href = risHref ?? safeHttpUrl(link[2]);
    return href
      ? <a
          key={index}
          href={href}
          target={risHref && onOpenRisSource ? undefined : '_blank'}
          rel="noopener noreferrer"
          data-ris-preview={risHref && onOpenRisSource ? 'true' : undefined}
          onClick={risHref && onOpenRisSource ? event => {
            event.preventDefault();
            onOpenRisSource(risHref);
          } : undefined}
          className="font-medium text-primary underline decoration-accent/50 underline-offset-2 hover:text-accent-dark"
        >{link[1]}</a>
      : <React.Fragment key={index}>{part}</React.Fragment>;
  }
  if (part.startsWith('**') && part.endsWith('**')) return <strong key={index} className="font-semibold">{part.slice(2, -2)}</strong>;
  if (part.startsWith('*') && part.endsWith('*')) return <em key={index}>{part.slice(1, -1)}</em>;
  return <React.Fragment key={index}>{part}</React.Fragment>;
});

const cells = (line: string): string[] => line.trim().replace(/^\||\|$/g, '').split('|').map(cell => cell.trim());
const tableDivider = (line: string): boolean => cells(line).every(cell => /^:?-{3,}:?$/.test(cell));
const special = (lines: string[], index: number): boolean => {
  const line = lines[index] ?? '';
  return /^\s*$/.test(line)
    || /^```/.test(line)
    || /^#{1,6}\s/.test(line)
    || /^\s*>\s?/.test(line)
    || /^\s*[-*+]\s+/.test(line)
    || /^\s*\d+[.)]\s+/.test(line)
    || /^\s*(?:---+|___+|\*\*\*+)\s*$/.test(line)
    || (line.includes('|') && tableDivider(lines[index + 1] ?? ''));
};

export const LegalMarkdown: React.FC<{
  content: string;
  className?: string;
  onOpenRisSource?: (url: string) => void;
  onOpenDocumentSource?: (documentId: string) => void;
}> = ({ content, className = '', onOpenRisSource, onOpenDocumentSource }) => {
  const lines = prepareRisMarkdown(content).replace(/\r\n?/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) { index += 1; continue; }

    if (/^```/.test(line)) {
      const language = line.slice(3).trim();
      const code: string[] = [];
      index += 1;
      while (index < lines.length && !/^```/.test(lines[index])) code.push(lines[index++]);
      if (index < lines.length) index += 1;
      blocks.push(<pre key={`code-${index}`} className="my-4 overflow-x-auto border border-warm-200 bg-warm-100 p-3 text-xs"><code data-language={language || undefined}>{code.join('\n')}</code></pre>);
      continue;
    }

    const heading = /^(#{1,6})\s+(.+)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      const body = inline(heading[2], onOpenRisSource, onOpenDocumentSource);
      const headingClass = level <= 2 ? 'mt-6 mb-3 font-serif text-xl font-semibold' : 'mt-5 mb-2 font-serif text-base font-semibold';
      blocks.push(React.createElement(`h${level}`, { key: `heading-${index}`, className: headingClass }, body));
      index += 1;
      continue;
    }

    if (/^\s*(?:---+|___+|\*\*\*+)\s*$/.test(line)) {
      blocks.push(<hr key={`hr-${index}`} className="my-5 border-warm-200" />);
      index += 1;
      continue;
    }

    if (line.includes('|') && tableDivider(lines[index + 1] ?? '')) {
      const headings = cells(line);
      index += 2;
      const rows: string[][] = [];
      while (index < lines.length && lines[index].includes('|') && lines[index].trim()) rows.push(cells(lines[index++]));
      blocks.push(
        <div key={`table-${index}`} className="my-4 overflow-x-auto border border-warm-200">
          <table className="min-w-full border-collapse text-left text-xs sm:text-sm">
            <thead className="bg-warm-100"><tr>{headings.map((cell, cellIndex) => <th key={cellIndex} className="border-b border-warm-200 px-3 py-2 font-semibold">{inline(cell, onOpenRisSource, onOpenDocumentSource)}</th>)}</tr></thead>
            <tbody>{rows.map((row, rowIndex) => <tr key={rowIndex} className="even:bg-warm-100/60">{row.map((cell, cellIndex) => <td key={cellIndex} className="border-b border-warm-200 px-3 py-2 align-top">{inline(cell, onOpenRisSource, onOpenDocumentSource)}</td>)}</tr>)}</tbody>
          </table>
        </div>,
      );
      continue;
    }

    if (/^\s*>\s?/.test(line)) {
      const quote: string[] = [];
      while (index < lines.length && /^\s*>\s?/.test(lines[index])) quote.push(lines[index++].replace(/^\s*>\s?/, ''));
      blocks.push(<blockquote key={`quote-${index}`} className="my-4 border-l-2 border-accent bg-warm-100 px-4 py-2 text-muted">{quote.map((item, itemIndex) => <React.Fragment key={itemIndex}>{inline(item, onOpenRisSource, onOpenDocumentSource)}{itemIndex < quote.length - 1 && <br />}</React.Fragment>)}</blockquote>);
      continue;
    }

    const unordered = /^\s*[-*+]\s+/.test(line);
    const ordered = /^\s*\d+[.)]\s+/.test(line);
    if (unordered || ordered) {
      const items: string[] = [];
      const pattern = unordered ? /^\s*[-*+]\s+/ : /^\s*\d+[.)]\s+/;
      while (index < lines.length && pattern.test(lines[index])) items.push(lines[index++].replace(pattern, ''));
      const List = ordered ? 'ol' : 'ul';
      blocks.push(<List key={`list-${index}`} className={`my-3 space-y-1 pl-6 ${ordered ? 'list-decimal' : 'list-disc'}`}>{items.map((item, itemIndex) => <li key={itemIndex}>{inline(item, onOpenRisSource, onOpenDocumentSource)}</li>)}</List>);
      continue;
    }

    const paragraph: string[] = [line.trim()];
    index += 1;
    while (index < lines.length && !special(lines, index)) paragraph.push(lines[index++].trim());
    blocks.push(<p key={`paragraph-${index}`} className="my-3 leading-7">{paragraph.map((item, itemIndex) => <React.Fragment key={itemIndex}>{inline(item, onOpenRisSource, onOpenDocumentSource)}{itemIndex < paragraph.length - 1 && <br />}</React.Fragment>)}</p>);
  }

  return <div className={`break-words text-sm text-primary ${className}`}>{blocks}</div>;
};

export default LegalMarkdown;
